import { useEffect, useRef, useState } from 'react';
import { FiX, FiEye, FiDownload } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';

const EXAM_LINK_OPEN_MODE = (import.meta.env.VITE_EXAM_LINK_OPEN_MODE || 'tab').toLowerCase();
const shouldOpenExamLinkInNewTab = EXAM_LINK_OPEN_MODE === 'tab';

const PublicExamDetailModal = ({ examId, onClose }) => {
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iframeModal, setIframeModal] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (examId) {
      fetchExam();
    }
  }, [examId]);

  useEffect(() => {
    if (!iframeModal) {
      return undefined;
    }

    const handleIframeMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      const data = event.data;
      const nextUrl = typeof data === 'string'
        ? data
        : data?.url || data?.href || data?.currentUrl;

      if (!nextUrl || typeof nextUrl !== 'string') {
        return;
      }

      console.log('[PublicExamDetailModal] Iframe postMessage URL:', {
        origin: event.origin,
        url: nextUrl,
        data,
      });

      setIframeModal((current) => {
        if (!current || current.url === nextUrl) {
          return current;
        }

        return {
          ...current,
          url: nextUrl,
        };
      });
    };

    window.addEventListener('message', handleIframeMessage);

    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [iframeModal]);

  const fetchExam = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/public/exams/${examId}`);
      setExam(response.data.data);
    } catch (error) {
      toast.error('Không thể tải thông tin ca chụp');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { label: 'Đã lên lịch', class: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'Đang thực hiện', class: 'bg-yellow-100 text-yellow-800' },
      completed: { label: 'Hoàn thành', class: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Đã hủy', class: 'bg-red-100 text-red-800' },
      synced: { label: 'Đã đồng bộ', class: 'bg-indigo-100 text-indigo-800' },
    };
    const config = statusConfig[status] || { label: status, class: 'bg-blue-100 text-blue-800' };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>{config.label}</span>;
  };

  const openViewer = () => {
    const viewerUrl = exam?.result_viewer_url || exam?.viewer_url;
    if (viewerUrl) {
      console.log('[PublicExamDetailModal] Open DICOM iframe URL:', viewerUrl);

      if (shouldOpenExamLinkInNewTab) {
        window.open(viewerUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      setIframeModal({
        title: 'Xem ảnh DICOM',
        url: viewerUrl,
      });
    } else {
      toast.warning('Không có link xem ảnh DICOM');
    }
  };

  const openPdf = () => {
    const pdfUrl = exam?.result_pdf_view_url || exam?.result_pdf_url;

    if (pdfUrl) {
      console.log('[PublicExamDetailModal] Open PDF iframe URL:', pdfUrl);

      if (shouldOpenExamLinkInNewTab) {
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      setIframeModal({
        title: 'Xem PDF kết quả',
        url: pdfUrl,
      });
    } else {
      toast.warning('Không có file kết quả PDF');
    }
  };

  const handleIframeLoad = (event) => {
    try {
      const nextUrl = event.currentTarget.contentWindow?.location?.href;
      console.log('[PublicExamDetailModal] Iframe loaded URL:', nextUrl);

      if (!nextUrl) {
        return;
      }

      setIframeModal((current) => {
        if (!current || current.url === nextUrl) {
          return current;
        }

        return {
          ...current,
          url: nextUrl,
        };
      });
    } catch (error) {
      console.log('[PublicExamDetailModal] Cannot read iframe URL after load:', {
        currentUrl: iframeModal?.url,
        reason: error?.message || String(error),
      });
      // Cross-origin iframes cannot expose their final URL to the parent page.
    }
  };

  if (!examId) return null;

  return (
    <>
    <div className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-lg font-bold text-slate-900">Chi tiết ca chụp</h2>
          <button 
            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
            onClick={onClose}
          >
            <FiX className="text-xl" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center min-h-[200px] gap-3">
              <div className="w-7 h-7 border-2 border-slate-200 border-t-primary rounded-full animate-spin"></div>
              <p className="text-slate-500 text-sm">Đang tải thông tin ca chụp...</p>
            </div>
          ) : !exam ? (
            <div className="text-center p-8 text-red-600 text-sm">Không tìm thấy ca chụp</div>
          ) : (
            <div className="space-y-4">
              {/* Thông tin bệnh nhân */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Thông tin bệnh nhân</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Họ tên:</span>
                    <span className="font-medium text-slate-900">{exam.patient_name || exam.patient_full_name || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Mã BN:</span>
                    <span className="font-medium text-slate-900">{exam.patient_id || '-'}</span>
                  </div>
                  {exam.patient_birth_date && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Ngày sinh:</span>
                      <span className="font-medium text-slate-900">{exam.patient_birth_date}</span>
                    </div>
                  )}
                  {exam.patient_age && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Tuổi:</span>
                      <span className="font-medium text-slate-900">{exam.patient_age}</span>
                    </div>
                  )}
                  {exam.patient_sex && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Giới tính:</span>
                      <span className="font-medium text-slate-900">
                        {exam.patient_sex === 'M' ? 'Nam' : exam.patient_sex === 'F' ? 'Nữ' : exam.patient_sex}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Thông tin ca chụp */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Thông tin ca chụp</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Modality:</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                      {exam.modality || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Trạng thái:</span>
                    {getStatusBadge(exam.status)}
                  </div>
                  {exam.scheduled_time && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Lịch hẹn:</span>
                      <span className="font-medium text-slate-900">
                        {new Date(exam.scheduled_time).toLocaleString('vi-VN')}
                      </span>
                    </div>
                  )}
                  {exam.study_date && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Ngày chụp:</span>
                      <span className="font-medium text-slate-900">
                        {exam.study_date} {exam.study_time ? exam.study_time.substring(0, 5) : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Thông tin kết quả */}
              {exam.result_conclusion && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">Kết luận</h3>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap bg-white p-3 rounded border border-slate-200">
                    {exam.result_conclusion}
                  </p>
                </div>
              )}

              {/* Nút hành động */}
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <button
                  onClick={openViewer}
                  disabled={!(exam?.result_viewer_url || exam?.viewer_url)}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FiEye /> Xem ảnh DICOM
                </button>
                <button
                  onClick={openPdf}
                  disabled={!(exam?.result_pdf_view_url || exam?.result_pdf_url)}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FiDownload /> Xem PDF kết quả
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {iframeModal && (
      <div
        className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 p-3 sm:p-5"
        onClick={() => setIframeModal(null)}
      >
        <div
          className="flex h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <h3 className="truncate pr-4 text-base font-semibold text-slate-900">{iframeModal.title}</h3>
            <button
              className="shrink-0 rounded p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
              onClick={() => setIframeModal(null)}
              aria-label="Đóng popup"
            >
              <FiX className="text-xl" />
            </button>
          </div>
          <iframe
            ref={iframeRef}
            src={iframeModal.url}
            title={iframeModal.title}
            className="min-h-0 flex-1 border-0"
            allow="fullscreen"
            allowFullScreen
            onLoad={handleIframeLoad}
          />
        </div>
      </div>
    )}
    </>
  );
};

export default PublicExamDetailModal;
